import { z } from 'zod';
import { LocalizedStringSchema } from './i18n';
import { FieldSchema } from './fields';
import { isSemver, isValidRange } from './semver';
import { FillSchema, StrokeSchema } from './style';
import { CardLookSchema, CardUseSchema } from './profile-card';

/**
 * Canvas access (API 4.0): `canvas.read.*` and `canvas.write.*` are independent; `all` covers every
 * object of the open canvas, `own` only the module's own objects — objects of its types and core
 * objects it created, until the user edits them (`createdByModule` on the record). The user may grant
 * `own` where the manifest asks for `all`.
 * Modules have no network at all (API 4.0): the worker runs under `connect-src 'none'`.
 * `files` (API 2.0): open and save files through the platform's dialogs, only on a user action.
 * `clipboard` (API 2.0): read and write the clipboard through the platform, only on a user action.
 * `mcp` (API 1.6): call tools of other modules (`platform.modules.*`) — given to AI gateways such as
 * the MCP module; a separate grant, the user sees it on the module page.
 * `feed` (API 2.5): show the module's tools (`contributes.feedTools`) in the Tools section of the feed.
 * `canvas.create` (API 2.6): make new canvases with content (`platform.canvases`) — converters, importers.
 * `keyboard` (API 4.6): keys go into the module's active live view and windows, and to its worker
 * (event `key`) while one object of a type with `capabilities.keyboard` is selected; without it the
 * views and windows of the module never keep the keyboard focus.
 * `fullscreen`, `pointer-lock` (API 4.6): the module's live views and windows may go full screen /
 * capture the mouse pointer.
 * `profile.cards` (API 4.10): create profile cards at run time (`platform.profileCards.create`).
 * `profile.stats` (API 4.10): the profile's counters in the live data of its cards (`data-vp-live="profile:…"`).
 */
export const PermissionSchema = z.enum(['canvas.read.all', 'canvas.read.own', 'canvas.write.all', 'canvas.write.own', 'files', 'clipboard', 'mcp', 'feed', 'canvas.create', 'keyboard', 'fullscreen', 'pointer-lock', 'profile.cards', 'profile.stats']);
export type Permission = z.infer<typeof PermissionSchema>;

export type CanvasAccessKind = 'read' | 'write';
export type CanvasAccess = 'all' | 'own' | 'none';
/** What a host method requires: a canvas access of either scope, or a plain permission. */
export type Requirement = `canvas.${CanvasAccessKind}` | Exclude<Permission, `canvas.${CanvasAccessKind}.${string}`>;

/** The canvas access a set of permissions gives. */
export function canvasAccess(perms: Iterable<Permission>, kind: CanvasAccessKind): CanvasAccess {
  // a granted set (the hot path: one check per object) is used as it is
  const set: ReadonlySet<Permission> = perms instanceof Set ? perms : new Set(perms);
  return set.has(`canvas.${kind}.all`) ? 'all' : set.has(`canvas.${kind}.own`) ? 'own' : 'none';
}

/** Whether the permissions meet a requirement (`canvas.read` is met by `canvas.read.all` and `.own`). */
export function meets(perms: Iterable<Permission>, req: Requirement): boolean {
  if (req === 'canvas.read' || req === 'canvas.write') return canvasAccess(perms, req === 'canvas.read' ? 'read' : 'write') !== 'none';
  return new Set(perms).has(req);
}

/**
 * The grants a request allows: the permission itself, and for an `all` canvas access also `own`
 * (the user narrows it at install).
 */
export function grantable(requested: Iterable<Permission>): Set<Permission> {
  const out = new Set<Permission>();
  for (const p of requested) {
    out.add(p);
    if (p === 'canvas.read.all') out.add('canvas.read.own');
    if (p === 'canvas.write.all') out.add('canvas.write.own');
  }
  return out;
}

/**
 * Grants kept across an update or a rollback: what was granted and is still asked for; a narrowed
 * `own` stays where the new manifest asks for `all`.
 */
export function keptGrants(requested: readonly Permission[], before: readonly Permission[]): Permission[] {
  const had = new Set(before);
  const out: Permission[] = [];
  for (const p of requested) {
    if (had.has(p)) out.push(p);
    else if (p === 'canvas.read.all' && had.has('canvas.read.own')) out.push('canvas.read.own');
    else if (p === 'canvas.write.all' && had.has('canvas.write.own')) out.push('canvas.write.own');
  }
  return out;
}

export const RenderModeSchema = z.enum(['A', 'B', 'C']);
export type RenderMode = z.infer<typeof RenderModeSchema>;

const localName = z.string().regex(/^[a-z][a-z0-9-]{0,63}$/);
const filePath = z
  .string()
  .max(200)
  .regex(/^[A-Za-z0-9_][A-Za-z0-9_./-]*$/)
  .refine((p) => !p.includes('..'), 'path traversal');

export const ObjectTypeContributionSchema = z.object({
  type: localName,
  name: LocalizedStringSchema,
  renderMode: RenderModeSchema,
  formatVersion: z.number().int().min(0),
  defaultSize: z.object({ w: z.number().positive().max(100000), h: z.number().positive().max(100000) }),
  defaultProps: z.record(z.unknown()).optional(),
  fields: z.array(FieldSchema).max(200).optional(),
  capabilities: z
    .object({
      resize: z.boolean().optional(),
      rotate: z.boolean().optional(),
      /**
       * The platform's style controls (fill, stroke, text, opacity) work for objects of this type;
       * the style is kept in the object's `style` and the renderer reads it from the record.
       */
      style: z.boolean().optional(),
      /**
       * API 2.2: primitives of this type may carry `action` — clickable areas (buttons, checkboxes)
       * that send `object.action` to the module.
       */
      actions: z.boolean().optional(),
      /** Object computes its state from incoming typed links. */
      computed: z.boolean().optional(),
      /** API 4.6: while one object of this type is selected, keys go to the module (event `key`; needs `keyboard`). */
      keyboard: z.boolean().optional(),
    })
    .optional(),
  /**
   * API 1.5: the object has real text (`props.text` + `props.spans`) that the platform draws over
   * the module's drawing, edits with its own editor (formatting included), finds in search, puts in
   * exports and fits the object to. `inset` — room around the text (object units), `fit` — grow the
   * object's height with the text, `style` — default text style of new objects.
   */
  text: z
    .object({
      inset: z.object({ top: z.number().min(0).max(10000), right: z.number().min(0).max(10000), bottom: z.number().min(0).max(10000), left: z.number().min(0).max(10000) }).partial().optional(),
      fit: z.boolean().optional(),
      style: z
        .object({
          fontSize: z.number().min(4).max(400),
          color: z.string().regex(/^#[0-9a-fA-F]{6}([0-9a-fA-F]{2})?$/),
          align: z.enum(['left', 'center', 'right']),
          valign: z.enum(['top', 'middle', 'bottom']),
          bold: z.boolean(),
        })
        .partial()
        .optional(),
    })
    .optional(),
  /** API 2.0: style of new objects (needs `capabilities.style`); the user changes it with the platform's controls. */
  defaultStyle: z.object({ fill: FillSchema, stroke: StrokeSchema.nullable(), opacity: z.number().min(0).max(1) }).partial().optional(),
  /**
   * API 2.0, render mode C: how the live view and its object fit each other. `none` — the view fills
   * the object (default); `grow` — the object takes the size of the view's content (`axis`: height
   * or both); `scale` — the object keeps its size, the content scales down to fit. `min`/`max` —
   * limits of the object's size in object units.
   */
  view: z
    .object({
      fit: z.enum(['none', 'grow', 'scale']).optional(),
      axis: z.enum(['height', 'both']).optional(),
      min: z.object({ w: z.number().positive().max(100000), h: z.number().positive().max(100000) }).partial().optional(),
      max: z.object({ w: z.number().positive().max(100000), h: z.number().positive().max(100000) }).partial().optional(),
    })
    .optional(),
  /**
   * API 2.0: drawing when zoomed out. By default an object of a module is drawn in full detail at any
   * zoom; `simplify: true` lets the platform replace small text with bars (faster on big boards).
   */
  lod: z.object({ simplify: z.boolean().optional() }).optional(),
  /**
   * API 2.1: where the properties of objects of this type open by default — `bar` (the top bar, as
   * everywhere) or `sidebar` (the right sidebar). The user can still move them either way; the
   * choice is remembered per module and wins over this default.
   */
  propertiesIn: z.enum(['bar', 'sidebar']).optional(),
  /** Platform operations that conflict with module work in progress (§9.4). */
  conflicts: z.array(z.enum(['move', 'resize', 'rotate', 'delete', 'style'])).optional(),
}).strict();
export type ObjectTypeContribution = z.infer<typeof ObjectTypeContributionSchema>;

export const ToolContributionSchema = z.object({
  id: localName,
  /** The type a frame-drawing tool creates; tools with a `gesture` create objects themselves. */
  objectType: localName.optional(),
  /**
   * API 2.2: the tool's gesture instead of drawing a frame: `click` (a point), `line` (from — to),
   * `path` (a freehand stroke). The module gets the points (`registerTool`) and creates objects itself.
   */
  gesture: z.enum(['click', 'line', 'path']).optional(),
  name: LocalizedStringSchema,
  /** Inline SVG markup or a file path inside the package. */
  icon: z.string().max(20000).optional(),
});

export const LinkTypeContributionSchema = z.object({
  type: localName,
  name: LocalizedStringSchema,
  directed: z.boolean(),
  /** Allowed endpoint object types (full type names); empty = any. */
  from: z.array(z.string()).optional(),
  to: z.array(z.string()).optional(),
});

/** A module object type usable as a DSkhema node form: `кэш: Redis (куб)` (docs/ru/DSKHEMA.md §5.2). */
export const DiagramShapeContributionSchema = z.object({
  objectType: localName,
  aliases: z.array(z.string().min(1).max(40).regex(/^[\p{L}\p{N}_-]+$/u)).min(1).max(10),
});

/** A font shipped in the module package (woff2/woff/ttf/otf files), usable on the canvas, in views and in SVG export. */
export const FontContributionSchema = z.object({
  family: z.string().min(1).max(64).regex(/^[\p{L}\p{N} _-]+$/u),
  files: z
    .array(
      z.object({
        path: filePath.refine((p) => /\.(woff2?|ttf|otf)$/i.test(p), 'font file expected'),
        weight: z.number().int().min(100).max(900).optional(),
        style: z.enum(['normal', 'italic']).optional(),
        unicodeRange: z.string().max(500).regex(/^[U+0-9A-Fa-f?, -]+$/).optional(),
      }),
    )
    .min(1)
    .max(16),
});
export type FontContribution = z.infer<typeof FontContributionSchema>;

export const CommandContributionSchema = z.object({
  id: localName,
  name: LocalizedStringSchema,
  objectTypes: z.array(localName).optional(),
});

/**
 * API 1.5: a button of the module in the platform's top bar, shown while objects the command
 * works on are selected. `icon` — inline SVG or a platform icon name; `active` — the button is
 * pressed while every selected object of those types has `props[prop] === equals`.
 */
export const ToolbarContributionSchema = z.object({
  command: localName,
  icon: z.string().max(20000).optional(),
  active: z.object({ prop: z.string().min(1).max(64), equals: z.union([z.string().max(200), z.number(), z.boolean(), z.null()]) }).optional(),
});
export type ToolbarContribution = z.infer<typeof ToolbarContributionSchema>;

/**
 * API 2.5: a tool of the module in the Tools section of the feed (the side panel), next to the
 * platform's own tools. Runs a command of the module that is not tied to object types, while a
 * canvas is open; shown only with the `feed` permission granted. `icon` — inline SVG or a platform
 * icon name, `hint` — kept on the manifest, not drawn in the feed. `home` (API 2.6) — the tool works where no canvas is
 * open too (home screen): its command gets an empty selection.
 */
export const FeedToolContributionSchema = z.object({
  command: localName,
  icon: z.string().max(20000).optional(),
  hint: LocalizedStringSchema.optional(),
  home: z.boolean().optional(),
}).strict();
export type FeedToolContribution = z.infer<typeof FeedToolContributionSchema>;

/**
 * API 3.0: a card of the module on the person's profile screen. The person adds it from the panel
 * library and places it on the tray. `view` — the module's HTML shown inside the card.
 * API 4.10: the card has no scripts; its interactive elements and live data are `data-vp-*` attributes
 * the platform handles, its effects are the platform's classes (docs: MODULE-API, profile cards).
 * `size` — the size of a freshly added card (px of the tray), `look` — the module's look of the card,
 * `uses` — every platform feature the card relies on (checked against its HTML at install).
 */
export const ProfileCardContributionSchema = z.object({
  id: z.string().regex(/^[a-z][a-z0-9-]{0,31}$/),
  title: LocalizedStringSchema,
  view: filePath,
  size: z.object({ w: z.number().min(120).max(760), h: z.number().min(84).max(483) }).strict().optional(),
  look: CardLookSchema.optional(),
  uses: z.array(CardUseSchema).max(30).optional(),
}).strict();
export type ProfileCardContribution = z.infer<typeof ProfileCardContributionSchema>;

/**
 * API 1.6: a tool of the module that AI gateways (modules with the `mcp` permission) may call:
 * `platform.registerMcpTool(name, fn)` handles it. `inputSchema` — JSON Schema of the arguments.
 */
export const McpToolContributionSchema = z.object({
  name: z.string().regex(/^[a-z][a-z0-9_]{0,63}$/),
  description: z.string().min(1).max(2000),
  inputSchema: z.record(z.unknown()).optional(),
});
export type McpToolContribution = z.infer<typeof McpToolContributionSchema>;

/**
 * API 2.2: a command of the module in the canvas context menu (shown when the command applies to the
 * selection, by its `objectTypes`).
 */
export const ContextMenuContributionSchema = z.object({ command: localName });

/**
 * API 2.2: a file format of the module in the canvas menu. `import` — the user picks files, the
 * module turns them into objects (`registerImport`, needs `canvas.write`); `export` — the module
 * turns the selection (or the whole canvas) into a file (`registerExport`, needs `canvas.read`).
 */
export const FormatContributionSchema = z.object({
  id: localName,
  kind: z.enum(['import', 'export']),
  name: LocalizedStringSchema,
  /** import: accepted files, e.g. ".csv", "text/csv". */
  accept: z.array(z.string().max(100).regex(/^(\.[\w-]+|[\w-]+\/[\w.+*-]+)$/)).max(10).optional(),
});
export type FormatContribution = z.infer<typeof FormatContributionSchema>;

/**
 * API 2.2: a ready board or group of objects the user inserts from the "More" menu: `file` — JSON in
 * the format of `objects.createTree` (coordinates relative to the template, own types and core ones).
 */
export const TemplateContributionSchema = z.object({
  id: localName,
  name: LocalizedStringSchema,
  description: LocalizedStringSchema.optional(),
  file: filePath.refine((p) => /\.json$/i.test(p), 'json file expected'),
});
export type TemplateContribution = z.infer<typeof TemplateContributionSchema>;

/**
 * Shortcuts the platform and the browser keep for themselves; a module cannot take them.
 * Module shortcuts must use Alt, or Ctrl together with Shift, so they never shadow single keys.
 */
export const RESERVED_SHORTCUTS: ReadonlySet<string> = new Set([
  'Ctrl+Alt+U', 'Ctrl+Alt+S', 'Ctrl+Alt+I', 'Ctrl+Alt+E', 'Ctrl+Shift+Z', 'Ctrl+Shift+G',
  'Ctrl+Shift+T', 'Ctrl+Shift+N', 'Ctrl+Shift+W', 'Ctrl+Shift+I', 'Ctrl+Shift+J', 'Ctrl+Shift+C', 'Ctrl+Shift+R', 'Ctrl+Shift+Q', 'Ctrl+Shift+B', 'Ctrl+Shift+Delete',
]);

/** API 2.2: a shortcut that runs a command of the module: `Alt+K`, `Ctrl+Alt+K`, `Ctrl+Shift+K` (letters, digits). */
export const ShortcutContributionSchema = z.object({
  command: localName,
  keys: z
    .string()
    .regex(/^(Ctrl\+)?(Alt\+)?(Shift\+)?[A-Z0-9]$/, 'keys like Alt+K, Ctrl+Alt+K or Ctrl+Shift+K')
    .refine((k) => k.includes('Alt+') || (k.startsWith('Ctrl+') && k.includes('Shift+')), 'a module shortcut needs Alt, or Ctrl together with Shift'),
});
export type ShortcutContribution = z.infer<typeof ShortcutContributionSchema>;

/**
 * API 2.0: a panel of the module not tied to an object (settings, lists, summaries): a button in the
 * platform's top-right bar opens a floating card with the module's HTML file (sandboxed like a view).
 * The module talks to it with `platform.postToPanel` / `on('panel.message')`.
 */
export const PanelContributionSchema = z.object({
  title: LocalizedStringSchema,
  /** Inline SVG or a platform icon name. */
  icon: z.string().max(20000).optional(),
  file: filePath.refine((p) => /\.html?$/i.test(p), 'html file expected'),
  width: z.number().int().min(200).max(900).optional(),
  height: z.number().int().min(120).max(1200).optional(),
});
export type PanelContribution = z.infer<typeof PanelContributionSchema>;

/**
 * API 2.3: a window of the module — a floating window over the platform with the module's HTML file
 * (sandboxed like a view), opened with `platform.windows.open(id)` on a user action, like the
 * diagram code window. A module may also open windows with HTML it makes at run time.
 */
export const WindowContributionSchema = z.object({
  id: z.string().regex(/^[a-z0-9][a-z0-9-]{0,39}$/),
  title: LocalizedStringSchema,
  file: filePath.refine((p) => /\.html?$/i.test(p), 'html file expected'),
  width: z.number().int().min(200).max(1600).optional(),
  height: z.number().int().min(120).max(1200).optional(),
  /** The user may resize the window (default true). */
  resizable: z.boolean().optional(),
  /** The rest of the platform waits until the window is closed (default false). */
  modal: z.boolean().optional(),
});
export type WindowContribution = z.infer<typeof WindowContributionSchema>;

/**
 * API 1.6: maturity of the module, shown as a badge next to its name: `experimental` (may change or
 * disappear), `beta` (works, still being polished), `test` (for testing the platform or the API).
 * No status = a regular module.
 */
export const ModuleStatusSchema = z.enum(['experimental', 'beta', 'test']);
export type ModuleStatus = z.infer<typeof ModuleStatusSchema>;

/** API 2.4: kinds of the publisher's links; each has its own icon in the app and the catalog. */
export const PUBLISHER_LINK_KINDS = ['website', 'repository', 'docs', 'support', 'email', 'github', 'gitlab', 'telegram', 'vk', 'x', 'youtube', 'discord', 'other'] as const;
export type PublisherLinkKind = (typeof PUBLISHER_LINK_KINDS)[number];

/**
 * API 2.4: a link to the publisher's resources — a repository, a site, a social network. Only https
 * (and mailto: for `email`): the app opens them in a new tab, never inside itself.
 */
export const PublisherLinkSchema = z
  .object({
    kind: z.enum(PUBLISHER_LINK_KINDS),
    url: z.string().max(500),
    /** Text instead of the address (the address is shown when there is none). */
    label: LocalizedStringSchema.optional(),
  })
  .strict()
  .superRefine((l, ctx) => {
    let u: URL | null = null;
    try {
      u = new URL(l.url);
    } catch {
      /* reported below */
    }
    const ok = l.kind === 'email' ? u?.protocol === 'mailto:' && /^mailto:[^\s@]+@[^\s@]+\.[^\s@]+$/.test(l.url) : u?.protocol === 'https:' && !!u.hostname && !u.username && !u.password;
    if (!ok) ctx.addIssue({ code: z.ZodIssueCode.custom, message: l.kind === 'email' ? 'an email link is mailto:name@example.com' : 'a link must be an https:// address', path: ['url'] });
  });
export type PublisherLink = z.infer<typeof PublisherLinkSchema>;

export const ModuleManifestSchema = z.object({
  manifestVersion: z.literal(1),
  id: z.string().regex(/^[a-z0-9]+(\.[a-z0-9-]+)+$/).max(128),
  name: LocalizedStringSchema,
  description: LocalizedStringSchema,
  icon: filePath.optional(),
  version: z.string().refine(isSemver, 'invalid semver'),
  buildDate: z.string().datetime({ offset: true }),
  author: z.string().min(1).max(200),
  apiVersion: z.string().refine(isValidRange, 'invalid version range'),
  /** API 1.6: maturity badge (see ModuleStatusSchema). */
  status: ModuleStatusSchema.optional(),
  /**
   * API 2.2: modules this one needs, with version ranges: `{ "org.example.tasks": "^3.0.0" }`. The module
   * is not installed without them, and they cannot be removed while it is installed.
   */
  dependencies: z.record(z.string().regex(/^[a-z0-9]+(\.[a-z0-9-]+)+$/).max(128), z.string().refine(isValidRange, 'invalid version range')).optional(),
  permissions: z.array(PermissionSchema).max(16),
  entry: filePath,
  contributes: z.object({
    objectTypes: z.array(ObjectTypeContributionSchema).max(100).optional(),
    tools: z.array(ToolContributionSchema).max(100).optional(),
    linkTypes: z.array(LinkTypeContributionSchema).max(100).optional(),
    commands: z.array(CommandContributionSchema).max(200).optional(),
    diagramShapes: z.array(DiagramShapeContributionSchema).max(100).optional(),
    fonts: z.array(FontContributionSchema).max(8).optional(),
    toolbar: z.array(ToolbarContributionSchema).max(24).optional(),
    /** API 1.6: settings of the module (not of an object), shown on its page in Settings. */
    settings: z.array(FieldSchema).max(50).optional(),
    /** API 1.6: tools for AI gateways (see McpToolContributionSchema). */
    mcpTools: z.array(McpToolContributionSchema).max(100).optional(),
    /** API 2.0 (see PanelContributionSchema). */
    panel: PanelContributionSchema.optional(),
    /** API 2.2 (see ContextMenuContributionSchema). */
    contextMenu: z.array(ContextMenuContributionSchema).max(12).optional(),
    /** API 2.2 (see ShortcutContributionSchema). */
    shortcuts: z.array(ShortcutContributionSchema).max(10).optional(),
    /** API 2.2 (see FormatContributionSchema). */
    formats: z.array(FormatContributionSchema).max(10).optional(),
    /** API 2.2 (see TemplateContributionSchema). */
    templates: z.array(TemplateContributionSchema).max(20).optional(),
    /** API 2.3 (see WindowContributionSchema). */
    windows: z.array(WindowContributionSchema).max(10).optional(),
    /** API 2.5 (see FeedToolContributionSchema). */
    feedTools: z.array(FeedToolContributionSchema).max(8).optional(),
    /** API 3.0 (see ProfileCardContributionSchema). */
    profileCards: z.array(ProfileCardContributionSchema).max(4).optional(),
  }).strict(),
  license: z.string().max(200).optional(),
  source: z.string().max(500).optional(),
  /** API 2.4: the publisher's resources (see PublisherLinkSchema). */
  links: z.array(PublisherLinkSchema).max(8).optional(),
})
  // API 2.0: an unknown field is an error, not silently ignored
  .strict();
export type ModuleManifest = z.infer<typeof ModuleManifestSchema>;
